import { useQuery } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { LightbulbIcon, ChevronLeft, ChevronRight } from 'lucide-react'
import { getSuggestedQestions } from '@/api/workstation'
import { Button } from '@/components/ui/button'
import { useRef, useState, useEffect } from 'react'

interface HorizontalSuggestedQuestionsProps {
  onQuestionClick: (question: string) => void
}

const arrowVariants = {
  initial: { opacity: 0, scale: 0.8 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.8 },
  hover: { 
    scale: 1.05, 
    opacity: 1
  }
}

export function HorizontalSuggestedQuestions({
  onQuestionClick,
}: HorizontalSuggestedQuestionsProps) {
  const {
    data: suggestedQuestions = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['suggestedQuestions'],
    queryFn: getSuggestedQestions,
    staleTime: 15 * 1000,
  })

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(true)

  const handleQuestionClick = (e: React.MouseEvent, question: string) => {
    e.preventDefault();
    e.stopPropagation();
    onQuestionClick(question);
  }

  const handleScrollLeft = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!scrollContainerRef.current) return;
    const scrollAmount = scrollContainerRef.current.clientWidth * 0.75;
    scrollContainerRef.current.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth'
    });
  };

  const handleScrollRight = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!scrollContainerRef.current) return;
    const scrollAmount = scrollContainerRef.current.clientWidth * 0.75;
    scrollContainerRef.current.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  };

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setShowLeftArrow(scrollLeft > 10);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    const currentRef = scrollContainerRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll);
      handleScroll();
    }
    
    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScroll);
      }
    }
  }, []);

  if (isLoading || isError || suggestedQuestions.length === 0) {
    return null;
  }

  return (
    <div className="w-full mb-3 relative">
      {showLeftArrow && (
        <div 
          className="absolute left-0 top-0 bottom-0 w-14 z-[5] pointer-events-none bg-gradient-to-r from-background to-transparent"
        />
      )}

      <AnimatePresence>
        {showLeftArrow && (
          <motion.div 
            className="absolute left-[-10px] top-[8px] z-10 flex items-center justify-center w-7 h-7 bg-background rounded-full shadow-sm cursor-pointer border border-gray-200 dark:border-gray-700"
            onClick={handleScrollLeft}
            variants={arrowVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            whileHover="hover"
            transition={{ duration: 0.2 }}
          >
            <ChevronLeft className="h-4 w-4 text-primary" />
          </motion.div>
        )}
      </AnimatePresence>
      
      <div className="overflow-hidden">
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto py-2 pl-1 pr-8 gap-3 scrollbar-hide"
          onScroll={handleScroll}
        >
          {suggestedQuestions.map((question, index) => (
            <div
              key={index}
              className='flex-shrink-0'
            >
              <Button
                variant='outline'
                size="sm"
                className='group h-8 whitespace-nowrap rounded-xl border-blue-200 bg-white px-4 py-1.5 text-xs font-normal text-foreground/80 shadow-sm transition-all hover:border-primary/50 hover:shadow-md dark:border-blue-800/30 dark:bg-blue-950/30'
                onClick={(e) => handleQuestionClick(e, question)}
                type="button"
              >
                <LightbulbIcon className='mr-1.5 h-3 w-3 flex-shrink-0 text-amber-400/80' />
                <span>{question}</span>
              </Button>
            </div>
          ))}
        </div>
      </div>
      
      {showRightArrow && (
        <div 
          className="absolute right-0 top-0 bottom-0 w-14 z-[5] pointer-events-none bg-gradient-to-l from-background to-transparent"
        />
      )}
      
      <AnimatePresence>
        {showRightArrow && (
          <motion.div 
            className="absolute right-[-10px] top-[8px] z-10 flex items-center justify-center w-7 h-7 bg-background rounded-full shadow-sm cursor-pointer border border-gray-300 dark:border-gray-700"
            onClick={handleScrollRight}
            variants={arrowVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            whileHover="hover"
            transition={{ duration: 0.2 }}
          >
            <ChevronRight className="h-4 w-4 text-primary" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
} 